import type { UiIconName } from '../scenes/ui/uiIcons';

export type SceneHubGroupId = 'play' | 'squad' | 'progress';

/** A scene's tile on the home hub. The hub model adds the live hint and enabled state. */
export interface SceneHubEntry {
  group: SceneHubGroupId;
  /** Position within the group; the hub layout reserves exactly these rows. */
  row: number;
  /** Also the navigation target `navigateHomeTarget` dispatches on. */
  itemId: string;
  label: string;
  icon: UiIconName;
}

interface SceneRegistryEntry {
  /** Property name on `SCENE_KEYS`. */
  id: string;
  /** Phaser scene key. */
  key: string;
  /**
   * Whether the scene resolves a responsive layout and may be shown on a phone viewport
   * (docs/UI_VIEWPORT_POLICY.md). A scene that is not phone-ready is blocked behind the gate.
   */
  phoneReady: boolean;
  hub?: SceneHubEntry;
}

/**
 * The one list of Phaser scenes. Order is the Phaser config order (first scene boots first), so
 * keep it stable. `SCENE_KEYS`, the Game scene array, the viewport gate and the home hub tiles all
 * derive from it; `Game.ts` adds the scene class per `id` in an exhaustive record.
 */
export const SCENE_REGISTRY = [
  { id: 'Boot', key: 'BootScene', phoneReady: true },
  { id: 'AssetPreload', key: 'AssetPreloadScene', phoneReady: true },
  { id: 'AssetGate', key: 'AssetGateScene', phoneReady: false },
  { id: 'Menu', key: 'MenuScene', phoneReady: true },
  { id: 'Settings', key: 'SettingsScene', phoneReady: true },
  { id: 'Auth', key: 'AuthScene', phoneReady: true },
  {
    id: 'ActiveSquad', key: 'ActiveSquadScene', phoneReady: true,
    hub: { group: 'squad', row: 0, itemId: 'active_squad', label: 'Active Squad', icon: 'squad' },
  },
  {
    id: 'OperatorRoster', key: 'OperatorRosterScene', phoneReady: true,
    hub: { group: 'squad', row: 1, itemId: 'collection', label: 'Collection', icon: 'collection' },
  },
  {
    id: 'Codex', key: 'CodexScene', phoneReady: true,
    hub: { group: 'progress', row: 2, itemId: 'codex', label: 'Codex', icon: 'codex' },
  },
  {
    id: 'Objectives', key: 'ObjectivesScene', phoneReady: true,
    hub: { group: 'progress', row: 0, itemId: 'objectives', label: 'Objectives', icon: 'objectives' },
  },
  {
    id: 'Recruitment', key: 'RecruitmentScene', phoneReady: true,
    hub: { group: 'squad', row: 2, itemId: 'recruitment', label: 'Recruitment', icon: 'recruit' },
  },
  { id: 'CampaignStory', key: 'CampaignStoryScene', phoneReady: true },
  { id: 'StagePrep', key: 'StagePrepScene', phoneReady: true },
  {
    id: 'LevelSelect', key: 'LevelSelectScene', phoneReady: true,
    hub: { group: 'play', row: 0, itemId: 'campaign', label: 'Campaign Map', icon: 'map' },
  },
  { id: 'Level', key: 'LevelScene', phoneReady: true },
  { id: 'UI', key: 'UIScene', phoneReady: true },
  { id: 'Results', key: 'ResultsScene', phoneReady: true },
  {
    id: 'Leaderboard', key: 'LeaderboardScene', phoneReady: true,
    hub: { group: 'progress', row: 1, itemId: 'leaderboard', label: 'Leaderboard', icon: 'leaderboard' },
  },
] as const satisfies readonly SceneRegistryEntry[];

export type SceneRegistryEntryOf = (typeof SCENE_REGISTRY)[number];
export type SceneId = SceneRegistryEntryOf['id'];
export type SceneHubItemId = Extract<SceneRegistryEntryOf, { hub: SceneHubEntry }>['hub']['itemId'];
